import { createRemoteJWKSet, jwtVerify } from "jose";

const jwksUrl = new URL("/.well-known/jwks.json", import.meta.env.VITE_TILTIFY_REDIRECT_URI);
// biome-ignore lint/suspicious/noConsole: JWKS URL debug
console.debug("[Tiltify] JWKS URL:", jwksUrl.toString());

const tiltifyJWKS = createRemoteJWKSet(jwksUrl);

/**
 * Verifies a Tiltify-issued JWS token and returns the decoded payload.
 * The public key is fetched from the backend's /.well-known/jwks.json endpoint,
 * matched by the `kid` in the JWT header.
 *
 * @param {string} jwt - The compact JWS string sent from the Tiltify Lambda.
 * @returns {Promise<{user: Object, profile: Object, tokens: Object}>}
 */
function verifyTiltifyJWT(jwt) {
  // biome-ignore lint/suspicious/noConsole: JWT verification debug
  console.debug("[Tiltify] Verifying JWT, first 40 chars:", jwt?.slice(0, 40));
  return jwtVerify(jwt, tiltifyJWKS).then((result) => {
    // biome-ignore lint/suspicious/noConsole: JWT verification debug
    console.debug("[Tiltify] JWT verified, payload:", result.payload);
    return result.payload;
  });
}

/**
 * Opens a popup window to start the Tiltify OAuth flow.
 * On success, calls `setUser` with `{ ...user, tokens }` where tokens contains
 * `accessToken`, `refreshToken`, `expiresIn`, and `createdAt`.
 *
 * @param {function({id: string, username: string, slug: string, tokens: Object}): void} setUser
 */
export function openTiltifyOAuthPopup(setUser = (_user) => {}) {
  const url = new URL("https://v5api.tiltify.com/oauth/authorize");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", import.meta.env.VITE_TILTIFY_CLIENT_ID);
  url.searchParams.set("redirect_uri", encodeURI(import.meta.env.VITE_TILTIFY_REDIRECT_URI));
  url.searchParams.set("scope", "public");

  const origin = new URL(import.meta.env.VITE_TILTIFY_REDIRECT_URI).origin;

  const win = window.open(url.toString());
  win.focus();

  const handleEvent = (event) => {
    // biome-ignore lint/suspicious/noConsole: popup message debug
    console.debug(
      "[Tiltify] message event — origin:",
      event.origin,
      "expected:",
      origin,
      "data preview:",
      String(event.data).slice(0, 80),
    );

    if (event.origin !== origin) {
      // biome-ignore lint/suspicious/noConsole: popup message debug
      console.debug("[Tiltify] Ignoring message from unexpected origin:", event.origin);
      return;
    }

    try {
      const data = JSON.parse(event.data);
      // biome-ignore lint/suspicious/noConsole: popup message debug
      console.debug("[Tiltify] Parsed JSON message, keys:", Object.keys(data));

      if (data.id) {
        // Legacy path: Lambda sent a plain JSON object with an `id` field.
        // biome-ignore lint/suspicious/noConsole: popup message debug
        console.debug("[Tiltify] Legacy path — plain user object");
        setUser(data);
        window.removeEventListener("message", handleEvent);
      } else if (data.jwt) {
        // Current path: Lambda sent JSON containing a signed JWT.
        // biome-ignore lint/suspicious/noConsole: popup message debug
        console.debug("[Tiltify] JSON-wrapped JWT path");
        verifyTiltifyJWT(data.jwt)
          .then((payload) => {
            setUser({ ...payload.user, tokens: payload.tokens });
            window.removeEventListener("message", handleEvent);
          })
          .catch((err) => {
            // biome-ignore lint/suspicious/noConsole: error logging for JWT verification failures
            console.error("[Tiltify] Failed to verify JWT from JSON message:", err);
          });
      } else {
        // biome-ignore lint/suspicious/noConsole: popup message debug
        console.warn("[Tiltify] JSON message had neither `id` nor `jwt`:", data);
      }
    } catch (_err) {
      // Current path: Lambda sent a raw JWT string (not JSON-wrapped).
      // biome-ignore lint/suspicious/noConsole: popup message debug
      console.debug("[Tiltify] Non-JSON message — treating as raw JWT string");
      verifyTiltifyJWT(event.data)
        .then((payload) => {
          setUser({ ...payload.user, tokens: payload.tokens });
          window.removeEventListener("message", handleEvent);
        })
        .catch((err) => {
          // biome-ignore lint/suspicious/noConsole: error logging for JWT verification failures
          console.error("[Tiltify] Failed to verify raw JWT:", err);
        });
    }
  };

  window.addEventListener("message", handleEvent);
}

/**
 * Returns `true` when the Tiltify access token has expired or will expire within
 * the given buffer window.
 *
 * @param {string|null|undefined} createdAt   - ISO-8601 timestamp from `tokens.createdAt`
 * @param {string|number|null|undefined} expiresIn - Lifetime in seconds from `tokens.expiresIn`
 * @param {number} [bufferSeconds=300]        - Seconds before actual expiry to treat as expired
 * @returns {boolean}
 */
export function isTiltifyTokenExpired(createdAt, expiresIn, bufferSeconds = 300) {
  if (!createdAt || !expiresIn) {
    // Can't determine expiry — assume expired so a refresh is attempted.
    return true;
  }
  const expiresAt = new Date(createdAt).getTime() + Number(expiresIn) * 1000;
  return Date.now() >= expiresAt - bufferSeconds * 1000;
}

/**
 * Calls the Tiltify Lambda refresh endpoint to exchange a refresh token for a
 * new access token.  The refresh URL is derived from `VITE_TILTIFY_REDIRECT_URI`
 * by replacing the last path segment (`getUserId`) with `refreshToken`.
 *
 * Resolves with `{ ...user, tokens }` on success, or `null` when the refresh
 * fails (network error, invalid refresh token, unexpected response shape, etc.).
 *
 * @param {string} refreshToken
 * @returns {Promise<{id: string, username: string, slug: string, tokens: Object}|null>}
 */
export function refreshTiltifyToken(refreshToken) {
  // Derive: https://.../api/tiltify/getUserId  →  https://.../api/tiltify/refreshUserToken
  const refreshUrl = import.meta.env.VITE_TILTIFY_REDIRECT_URI.replace(
    /\/[^/]+$/,
    "/refreshUserToken",
  );
  // biome-ignore lint/suspicious/noConsole: refresh debug
  console.debug(
    "[Tiltify] Refresh URL:",
    refreshUrl,
    "| VITE_TILTIFY_REDIRECT_URI:",
    import.meta.env.VITE_TILTIFY_REDIRECT_URI,
  );

  return fetch(refreshUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  })
    .then((res) => {
      if (!res.ok) {
        return null;
      }
      return res.json();
    })
    .then((data) => {
      if (!data?.jwt) {
        return null;
      }
      return verifyTiltifyJWT(data.jwt).then((payload) => ({
        ...payload.user,
        tokens: payload.tokens,
      }));
    })
    .catch((err) => {
      // biome-ignore lint/suspicious/noConsole: error logging for token refresh failures
      console.error(
        "[Tiltify] Token refresh failed — URL was:",
        refreshUrl,
        "| Error:",
        err?.message ?? err,
      );
      return null;
    });
}

// ---------------------------------------------------------------------------
// Console debug utilities — exposes helpers on window.__playlive.tiltify so
// you can manually test from the browser console, e.g.:
//   window.__playlive.tiltify.refreshToken("myRefreshToken").then(console.log)
//   window.__playlive.tiltify.verifyJWT("eyJ...").then(console.log)
// ---------------------------------------------------------------------------
if (typeof window !== "undefined") {
  window.__playlive = window.__playlive ?? {};
  window.__playlive.tiltify = {
    /**
     * Manually trigger a token refresh.
     * @param {string} refreshToken
     * @returns {Promise<Object|null>}
     */
    refreshToken: (refreshToken) => refreshTiltifyToken(refreshToken),
    /**
     * Manually verify a raw JWT string.
     * @param {string} jwt
     * @returns {Promise<Object>}
     */
    verifyJWT: (jwt) => verifyTiltifyJWT(jwt),
    /**
     * Log the JWKS URL that was computed at module load time.
     */
    jwksUrl: jwksUrl.toString(),
  };
  // biome-ignore lint/suspicious/noConsole: debug util registration
  console.debug("[Tiltify] Debug utils available at window.__playlive.tiltify");
}
